'use client'

import DiscoveryLogoSection from '@components/app/DiscoverySection'
import BookADemoButton from '@components/app/ForResearchCoordinatorsPage/BookADemoButton'
import FindPatientsButton from '@components/app/ForResearchCoordinatorsPage/FindPatientsButton'
import { NavBar } from '@components/app/NavBar'
import { FooterCTA } from '@components/app/SiteStaffPortal/Landers/FooterCTA'
import { RollingPatientCards } from '@components/app/SiteStaffPortal/Landers/RollingPatientCards'
import { TestimonialCard } from '@components/app/SiteStaffPortal/Landers/TestimonialCard'
import { Icon } from '@components/primitives/Icon'
import { CloudflareImage } from '@components/primitives/Image/Cloudflare'
import { Text } from '@components/primitives/Text'
import { faBallotCheck } from '@fortawesome/pro-solid-svg-icons/faBallotCheck'
import { faUserChart } from '@fortawesome/pro-solid-svg-icons/faUserChart'
import { faUsersMedical } from '@fortawesome/pro-solid-svg-icons/faUsersMedical'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import {
  hero,
  patientTable,
  preScreener,
  trialCards,
} from '@modules/images/pages/for-research-coordinators'
import { AnimatedSvgGradient } from '../HomePage/HomeHero/AnimatedSvgGradient.client'
import { ellipseConfig } from '../HomePage/HomeHero/consts'

export default function ForResearchCoordinators({
  demoUrl,
}: {
  demoUrl: string
}) {
  const trackHeroCtaClick = () =>
    trackEvent(TrackingEvents.RESEARCHER_LANDER_CTA_CLICKED, {
      source: TrackingSources.RESEARCHER_LANDER_HERO,
    })

  const trackFeatureCtaClick = () =>
    trackEvent(TrackingEvents.RESEARCHER_LANDER_CTA_CLICKED, {
      source: TrackingSources.RESEARCHER_LANDER_FEATURE,
    })

  const trackFooterCtaClick = () =>
    trackEvent(TrackingEvents.RESEARCHER_LANDER_CTA_CLICKED, {
      source: TrackingSources.RESEARCHER_LANDER_FOOTER,
    })

  return (
    <>
      <section className='relative overflow-hidden'>
        <NavBar
          className='mx-auto w-full px-4'
          linkActive='researcher'
          transparentBackground={true}
          unstyled={true}
        />
        <div className='relative z-[8] mx-auto flex max-w-screen-xl flex-col items-center justify-between gap-0 px-4 lg:flex-row lg:gap-12'>
          {/* hero left content */}
          <div className='my-8 flex flex-col gap-8'>
            <div className='flex flex-col gap-0'>
              <h1 className='text-nowrap text-left text-5xl font-bold leading-[125%] tracking-tighter text-black md:text-7xl md:leading-[115%] md:tracking-tight'>
                Fill your trials.
                <br />
                Power your sites.
              </h1>
            </div>
            <h2 className='max-w-[485px] text-left text-base leading-[155%] text-neutral800 md:text-lg'>
              The only self-serve recruitment marketplace. 1,000,000 patients
              used Power to look for clinical trials last year. We use AI to
              pre-screen patients and connect them directly with sites.
            </h2>
            <div className='flex flex-col gap-4 md:flex-row'>
              <div className='w-full md:max-w-[320px]'>
                <FindPatientsButton
                  className='h-14 w-full'
                  onClick={trackHeroCtaClick}
                />
                <Text
                  className='mt-2 text-center text-neutral600'
                  element='p'
                  styleName='p-tiny'
                  value='Try it for free. No technical set up.'
                />
              </div>
              <div className='w-full rounded-full md:max-w-[180px]'>
                <BookADemoButton
                  className='h-14 w-full rounded-full border-2'
                  demoUrl={demoUrl}
                />
              </div>
            </div>
          </div>

          {/* hero right desktop content */}
          <div className='hidden md:block md:w-full'>
            <RollingPatientCards />
          </div>

          {/* hero right mobile content */}
          <div className='block w-full md:hidden'>
            <CloudflareImage
              alt={'Patient Requesting Trial'}
              className={'object-cover'}
              height={3000}
              imageId={hero}
              width={3000}
            />
          </div>
        </div>

        <AnimatedSvgGradient
          className='absolute left-0 top-0 z-[5] block size-full opacity-70'
          ellipses={ellipseConfig}
        />
      </section>

      {/* 3 Trust Booster Metrics */}
      <section className='bg-neutral900 py-8 lg:py-20'>
        <div className='mx-auto flex max-w-screen-xl flex-col justify-between gap-4 px-4 lg:flex-row lg:gap-0'>
          <div className='w-full lg:mb-0 lg:w-[28%]'>
            <Text
              className='text-white'
              element='h4'
              styleName='h4'
              value='Patients are already using Power to look for clinical trials'
            />
          </div>

          <div>
            <Text className='text-white' styleName='h3' value='>1MM' />
            <Text
              className='text-neutral400'
              styleName='p'
              value={`Patients searched in ${new Date().getFullYear()}`}
            />
          </div>

          <div>
            <Text className='text-white' styleName='h3' value='>6k' />
            <Text
              className='text-neutral400'
              styleName='p'
              value="Trials where we've referred patients"
            />
          </div>
          <div>
            <Text className='text-white' styleName='h3' value='Up to 24%' />
            <Text
              className='text-neutral400'
              styleName='p'
              value='Lift in enrollment rate'
            />
          </div>
        </div>
      </section>

      {/* 3 value prop */}
      <section className='mx-auto w-full max-w-screen-xl px-4 py-20'>
        <div className='flex justify-center'>
          <Text
            className='mb-10 text-center text-neutral900'
            element='h2'
            styleName='h2'
            value='Work smarter, not harder'
          />
        </div>
        <div className='flex flex-col justify-between gap-4 lg:flex-row '>
          {/* 1st value */}
          <div className='flex basis-1/3 flex-col items-center'>
            <div className='flex size-14 items-center justify-center rounded-2xl border-2 border-primary100 bg-primary50'>
              <Icon className='text-primary700' icon={faUserChart} />
            </div>
            <Text
              className='mt-4 text-neutral900'
              element='h4'
              styleName='h4'
              value='95% new patients'
            />
            <Text
              className='mt-2 text-center text-neutral600'
              element='p'
              styleName='p-small'
              value='Reach the patients not yet at your site. Power enables access to clinical trials for all patients, which means you have access to a new patient pool.'
            />
          </div>

          {/* 2nd value */}
          <div className='flex basis-1/3 flex-col items-center'>
            <div className='flex size-14 items-center justify-center rounded-2xl border-2 border-primary100 bg-primary50'>
              <Icon className='text-primary700' icon={faBallotCheck} />
            </div>
            <Text
              className='mt-4 text-neutral900'
              element='h4'
              styleName='h4'
              value='Screen 2x more'
            />
            <Text
              className='mt-2 text-center text-neutral600'
              element='p'
              styleName='p-small'
              value='Screenings matter, not just leads. Work with high-intent, EMR-verified patients who are actively looking for trials (and actually answer the phone).'
            />
          </div>

          {/* 3rd value */}
          <div className='flex basis-1/3 flex-col items-center'>
            <div className='flex size-14 items-center justify-center rounded-2xl border-2 border-primary100 bg-primary50'>
              <Icon className='text-primary700' icon={faUsersMedical} />
            </div>
            <Text
              className='mt-4 text-neutral900'
              element='h4'
              styleName='h4'
              value='40% diverse patients'
            />
            <Text
              className='mt-2 text-center text-neutral600'
              element='p'
              styleName='p-small'
              value='You read that right. Unlike traditional campaigns that have 15% diverse representation, 40% of our patients are non-white. Inclusive research starts here.'
            />
          </div>
        </div>
      </section>

      {/* Feature UI Demo  */}
      <section className='mx-auto w-full max-w-screen-xl px-4 pb-20'>
        <div className='mb-8 flex flex-col items-center'>
          <Text
            className='mb-2 text-center text-neutral900'
            element='h2'
            styleName='h2'
            value='Power is built for busy sites'
          />
          <Text
            className='mb-4 text-center text-neutral600'
            element='p'
            styleName='p'
            value='Start matching with patients today. No technical set up. Get started for free.'
          />

          <div className='flex flex-col gap-2 md:flex-row md:gap-4'>
            <FindPatientsButton
              className='w-[320px] rounded-full'
              onClick={trackFeatureCtaClick}
            />

            <BookADemoButton
              className='w-[320px] rounded-full border-2 sm:w-[200px]'
              demoUrl={demoUrl}
            />
          </div>
        </div>

        {/* feature #1 demo */}
        <div className='mb-6 flex flex-col justify-between overflow-hidden rounded-3xl border border-neutral200 bg-neutral100 md:flex-row'>
          <div className='max-w-[496px] pl-6 pt-6 md:pl-8 md:pt-8'>
            <Text
              className='mb-1 text-neutral900'
              element='h4'
              styleName='h4'
              value='Verify your trial site on our marketplace and start meeting patients today'
            />
            <Text
              className=' text-neutral600'
              element='p'
              styleName='p'
              value='Join our marketplace in 5 minutes by verifying your site. Update your profile so patients have all of the latest information they need to find you.'
            />
          </div>

          <div className='w-full pl-6 pt-4 md:pl-8 md:pt-8'>
            <CloudflareImage
              alt={'Trial Cards'}
              className={'object-cover'}
              height={1472}
              imageId={trialCards}
              width={2464}
            />
          </div>
        </div>

        <div className='flex flex-col gap-6 md:flex-row'>
          {/* feature #2 demo */}
          <div className='flex basis-1/2 flex-col justify-between overflow-hidden rounded-3xl border border-neutral200 bg-neutral100'>
            <div className='px-6 pt-6 md:px-8 md:pt-8'>
              <Text
                className='mb-1 text-neutral900'
                element='h4'
                styleName='h4'
                value='Start meeting EMR-verified and engaged patients in half the time'
              />
              <Text
                className=' text-neutral600'
                element='p'
                styleName='p'
                value='We know how time consuming it is to call through databases and collect medical records. Power referrals are EMR-verified and AI matched to your studies, so you can contact qualified patients and cut your screening time in half.'
              />
            </div>
            <div className='w-full pl-6 pt-4 md:pl-8 md:pt-6'>
              <CloudflareImage
                alt={'Patient Table'}
                className={'object-cover'}
                height={1400}
                imageId={patientTable}
                width={2384}
              />
            </div>
          </div>

          {/* feature #3 demo */}
          <div className='flex basis-1/2 flex-col justify-between overflow-hidden rounded-3xl border border-neutral200 bg-neutral100'>
            <div className='px-6 pt-6 md:px-8 md:pt-8'>
              <Text
                className='mb-1 text-neutral900'
                element='h4'
                styleName='h4'
                value='Change your pre-screener in real time to accelerate recruitment'
              />
              <Text
                className=' text-neutral600'
                element='p'
                styleName='p'
                value='See exactly where patients are dropping off, and update your pre-screener questions in real time. No extra costs or emailing back-and-forth. Just do it in Power whenever you need to.'
              />
            </div>
            <div className='w-full px-6 pt-4 md:px-8 md:pt-6'>
              <CloudflareImage
                alt={'Pre-screener Question Builder'}
                className={'object-cover'}
                height={1020}
                imageId={preScreener}
                width={1644}
              />
            </div>
          </div>
        </div>
        <DiscoveryLogoSection />
      </section>

      {/* Testimonial */}
      <section className='mx-auto w-full max-w-screen-xl px-4 pb-20'>
        <div className='mb-10 flex justify-center'>
          <Text
            className='text-center text-neutral900'
            element='h2'
            styleName='h2'
            value='Sites and patients love using Power'
          />
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
          {/* col 1  */}
          <ul className='space-y-4'>
            <TestimonialCard
              personName='Magic Foundation'
              personTitle='Patient Foundation'
              personType='Foundation'
              quote='The site is so easy to navigate and LOVE that you can look it up by either disorder, state or even the specific drug.'
            />
            <TestimonialCard
              personName='Raina S.'
              personTitle='Researcher'
              personType='Supplier'
              quote="We spend a lot of time trying to reach potential participants, so for them to have one location that they can sift through in a way that's understandable for them I think is awesome and should have happened a long time ago."
            />
            <TestimonialCard
              personName='Ava'
              personTitle='Patient'
              personType='Patient'
              quote='A friend shared your link in our online patient community, and I found a stem cell trial in my city. I was planning to travel to Latin America for treatment. This is SO much easier.'
            />
          </ul>

          {/* col 2 */}
          <ul className='hidden space-y-4 md:block'>
            <TestimonialCard
              personName='Mark L.'
              personTitle='Patient'
              personType='Patient'
              quote="I have suffered with depression since my early teens. My depression is drug resistant; I've been through most in multiple classes, primarily SSRIs. Despite suffering with this for most of my life, I have never quit trying to find a cure."
            />
            <TestimonialCard
              personName='Karen F.'
              personTitle='Researcher'
              personType='Supplier'
              quote="It's so hard to turn people away when they don't fit the inclusion criteria. Having inclusion criteria on your site and giving patients an odds of qualifying is really great to see. I am really impressed by what your team has created."
            />

            <TestimonialCard
              personName='Stronger than Sarcoidosis'
              personTitle='Patient Foundation'
              personType='Foundation'
              quote='Clinical trials are always so hard to understand. Power is supporting Stronger than Sarcoidosis to help patients understand what each clinical trial is about. Simplifying clinical trials benefits everyone involved.'
            />
          </ul>

          {/* col 3 */}
          <ul className='hidden space-y-4 lg:block'>
            <TestimonialCard
              personName='Christine K.'
              personTitle='Top 5 CRO'
              personType='Supplier'
              quote='We found you on the front page of Google for ADHD and wanted to understand how to partner.'
            />
            <TestimonialCard
              personName='Raymond A Wood Foundation'
              personTitle='Patient Foundation'
              personType='Foundation'
              quote='Power features a clean and easy to use interface that allows patients to understand what trials are available and details of eligibility criteria, objectives, and study design along with trial locations. We serve a rare disease patient population and this has been a huge asset to our patients.'
            />
            <TestimonialCard
              personName='Luisa R.'
              personTitle='Clinical Research Coordinator'
              personType='Supplier'
              quote='This is terrific! Even with years of experience I can find clinicaltrials.gov hard to navigate. I always redirect my patients elsewhere.'
            />
          </ul>
        </div>
      </section>

      {/* Footer CTA */}
      <section className='mx-auto w-full max-w-screen-xl px-0 pb-0 md:px-4 md:pb-20'>
        <FooterCTA
          headline='Match with patients who are looking for your trial'
          subtitle='Join our marketplace in minutes, verify your trial info and start matching with patients today.'
        >
          <div className='relative z-[9] flex flex-col items-center justify-center gap-6 md:flex-row'>
            <FindPatientsButton
              className='w-[280px] rounded-full bg-white'
              onClick={trackFooterCtaClick}
            />
            <BookADemoButton
              className='w-[280px] rounded-full border-2 border-white bg-white'
              demoUrl={demoUrl}
            />
          </div>
        </FooterCTA>
      </section>
    </>
  )
}
